import _ from 'lodash';
import { StoryHistory } from 'client/utils';
import type {
	CardData,
	CardDataWithStepInfo,
	StoryModel,
	StoryStep,
	StorySymbols,
	StoryVersions,
	StoryVersionType,
} from 'types/story';
import { IS_DEVEL } from 'common/utils/environment';
import { CountType } from 'common/utils/facades/navigation-card-facade';
import { getAssetURL } from 'common/utils/assets';

const VERSIONS = {
	latest: 'latest',
	published: 'published',
	archived: 'archived',
	unpublished: 'unpublished',
} as const;

export type RequestMainRowData = {
	storyId: StoryModel['id'];
	name: StoryModel['name'];
	organizationId: StoryModel['organizationId'];
	type: StoryModel['type'];
	language: StoryModel['language'];
	tags: StoryModel['tags'];
	teamId?: StoryModel['teamId'];
};

/**
 * Pick data from story object that needed to update story
 * @param data
 */
export function requestMainRowData(
	data: Pick<StoryModel, 'id' | 'name' | 'organizationId' | 'type' | 'language' | 'tags' | 'teamId'>
): RequestMainRowData {
	return {
		storyId: data.id,
		name: data.name,
		organizationId: data.organizationId,
		type: data.type,
		language: data.language,
		tags: data.tags,
		teamId: data.teamId || undefined,
	};
}

/**
 * @param name
 * @param storyId
 * @param teamId
 * @param organizationId
 * @param storyData
 * @constructor
 */
export class StoryFacade {
	static VERSIONS = VERSIONS;

	static getVersionTimestamp(version: StoryVersionType) {
		if (version.version === StoryFacade.VERSIONS.latest || version.version === StoryFacade.VERSIONS.published) {
			return +new Date(version.updatedAt);
		}

		return +version.version.split('_').slice(-1)[0];
	}

	static getSortedVersions(versions: StoryVersions) {
		return Object.values(versions).sort(
			(a, b) => StoryFacade.getVersionTimestamp(b) - StoryFacade.getVersionTimestamp(a)
		);
	}

	base: StoryModel;

	// todo: story can be undefined. need change a type and do a big refactor
	story: StoryVersionType;

	constructor(story: StoryModel, defaultVersion: string = StoryFacade.VERSIONS.latest) {
		this.base = story;
		this.story = this.getVersion(defaultVersion);
	}

	getVersion(version: string): StoryVersionType {
		return _.get(this.base, ['storyVersions', version]);
	}

	setVersion(version: string) {
		this.story = this.getVersion(version);
		return this;
	}

	get isCardEditorAutoSyncEnabled() {
		return this.data.cardEditorAutoSync ?? !IS_DEVEL;
	}

	/**
	 * Provide data in the format required for an update request
	 * @return {{name: *, storyData: *, storyId: *}}
	 */
	get requestData() {
		return {
			storyId: this.storyId,
			storyData: this.data,
			settings: this.settings,
		};
	}

	get requestMainRowData() {
		return requestMainRowData(this.base);
	}

	get language() {
		return this.base.language;
	}

	get settings() {
		return this.story.settings || {};
	}

	get name() {
		return this.base.name;
	}

	get isPublished() {
		return this.story.version === VERSIONS.published || this.base.status === 'published';
	}

	get data() {
		return this.story.data || {};
	}

	get steps(): StoryVersionType['data']['steps'] {
		return this.data.steps || [];
	}

	get elements(): StoryVersionType['data']['elements'] {
		return this.data.elements || [];
	}

	get mediaQuery(): StoryVersionType['data']['mediaQuery'] {
		return this.data.mediaQuery;
	}

	get storyId() {
		return this.base.id;
	}

	get symbols(): StorySymbols {
		return this.data.symbols || {};
	}

	get storyScore() {
		const sHistory = new StoryHistory({ storyId: this.storyId });
		return {
			[CountType.score]: () => sHistory.totalScore(this.settings, this.cards),
			[CountType.character]: () => sHistory.totalCharacterPoints(this.settings),
		};
	}

	get submittedFormData() {
		return new StoryHistory({ storyId: this.storyId }).submittedFormData;
	}

	get totalVisitedCards() {
		return new StoryHistory({ storyId: this.storyId }).cardsPlayed.length;
	}

	/**
	 * Get cards array
	 * @return {Array}
	 */
	get cards(): CardData[] {
		return _.reduce<StoryStep, CardData[]>(
			this.steps,
			(result, step) => {
				return result.concat(step.cards);
			},
			[]
		);
	}

	/**
	 * Get cards array
	 * @return {Array}
	 */
	get cardsWithStepInfo(): CardDataWithStepInfo[] {
		return _.reduce<StoryStep, (CardData & { step: { _id: StoryStep['_id'] } })[]>(
			this.steps,
			(result, step) => {
				const cardsWithStep = _.map(step.cards, card => ({
					...card,
					step: { ..._.pick(step, ['_id']) },
				}));

				return result.concat(cardsWithStep);
			},
			[]
		);
	}

	get dataByFirstCard() {
		const result: { card: CardData | null; step: StoryStep | null } = { card: null, step: null };
		const { steps } = this;

		steps.some(step => {
			return (step.cards || []).some(card => {
				const isMatch = _.has(card, '_id');
				if (isMatch) {
					result.card = card;
					result.step = step;
				}
				return isMatch;
			});
		});
		return result;
	}

	getCardScreenshot = (cardId: string) => {
		const { defaultPlatform } = this.mediaQuery;
		const asset =
			this.base.storyVersions[VERSIONS.published]?.settings.screenshot?.[cardId]?.[defaultPlatform] ||
			this.base.storyVersions[VERSIONS.latest]?.settings.screenshot?.[cardId]?.[defaultPlatform];

		return { asset, url: getAssetURL(asset) };
	};

	canUseThirdPartyCookies() {
		const isSubdomainConnected = this.base.domainId !== null;
		const isEmbed = this.base.type === 'embed';
		return (isEmbed && isSubdomainConnected) || !isEmbed;
	}

	getDataByCardId(cardId): { card?: CardData; step?: StoryStep } {
		const { steps } = this;
		const result: { card?: CardData; step?: StoryStep } = {};

		steps.some(step => {
			return _.get(step, 'cards', []).some(card => {
				const isMatch = card._id === cardId;
				if (isMatch) {
					result.card = card;
					result.step = step;
				}
				return isMatch;
			});
		});

		return result;
	}

	getNextDefaultCard(step: StoryStep): CardData | undefined {
		const stepIndex = _.findIndex(this.steps, ['_id', step._id]);

		if (stepIndex < 0) {
			return undefined;
		}

		const nextStepWithCards = _.find(_.slice(this.steps, stepIndex + 1), s => s.cards.length > 0);

		return nextStepWithCards?.cards[0];
	}

	// get text with a replaced supported dynamic tet variables
	getTransformedDynamicText(text: string = '') {
		const scoreResults = this.storyScore[CountType.score]();

		return text
			.replace('$score', `${Math.ceil(scoreResults.scoreRelative) ?? '0'}/100`)
			.replace('$answers', `${scoreResults.cardsScored ?? '0'}`)
			.replace('$correct', `${scoreResults.answersCorrect ?? '0'}`);
	}
}
